/**
 * 菜单路径工具：统一从接口返回的菜单 path 解析路由，避免写死路径前缀
 */
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'
import { stripLocalePrefix, withLocalePrefix } from '@/components/portal/common/utils/localePath'

function findMenuNodeById(menus = [], menuId) {
  if (menuId == null || menuId === '') return null
  const target = String(menuId)

  const walk = (nodes = []) => {
    for (const node of nodes) {
      if (String(node.id) === target) return node
      const nested = walk(node.children)
      if (nested) return nested
    }
    return null
  }

  return walk(menus)
}

/** 标准化菜单/路由路径 */
export function normalizeMenuPath(path) {
  if (!path) return ''
  const trimmed = String(path).trim()
  if (!trimmed || trimmed === HOME_PATH) return HOME_PATH
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  if (withSlash === HOME_PATH) return HOME_PATH
  return withSlash.replace(/\/+$/, '') || HOME_PATH
}

/** 去掉语言前缀后的内容路径，用于匹配 CMS 菜单 */
export function normalizeContentPath(path) {
  return stripLocalePrefix(normalizeMenuPath(path)) || HOME_PATH
}

export const PORTAL_ENTER_MENU = 'menu'
export const PORTAL_NOT_FOUND_PATH = '/404'

/** 顶栏/mega 菜单跳转：带 state，不污染 URL。空 path 进 404，不误跳首页 */
export function buildPortalMenuLocation(path, locale) {
  const normalized = normalizeMenuPath(path)
  if (!normalized) return PORTAL_NOT_FOUND_PATH
  const localized = withLocalePrefix(normalized, locale)
  if (normalizeContentPath(localized) === HOME_PATH) return localized
  return { path: localized, state: { portalEnter: PORTAL_ENTER_MENU } }
}

/** 是否由顶栏菜单进入（router-link state 或 history.state） */
export function isPortalMenuEnter(to) {
  if (to?.state?.portalEnter === PORTAL_ENTER_MENU) return true
  if (typeof window === 'undefined') return false
  return window.history?.state?.portalEnter === PORTAL_ENTER_MENU
}

/** 从菜单路径提取末段 slug */
export function slugFromMenuPath(path) {
  if (!path) return ''
  const normalized = String(path).trim().replace(/^\//, '')
  const parts = normalized.split('/').filter(Boolean)
  return parts[parts.length - 1] || ''
}

/** 读取菜单节点的完整路径 */
export function getMenuNodePath(node) {
  return normalizeMenuPath(node?.path)
}

/**
 * 从子菜单 path 推导公共父路径。
 * CMS 里部分根栏目（如新闻中心）自身 path 为空，只在子分类上配置了 /news/company。
 */
export function deriveMenuPathFromChildren(node) {
  const childPaths = (node?.children || [])
    .filter((item) => isPortalMenuVisible(item))
    .map((item) => normalizeMenuPath(item?.path))
    .filter((path) => path && path !== HOME_PATH)

  if (!childPaths.length) return ''

  const parents = childPaths
    .map((path) => {
      const parts = path.split('/').filter(Boolean)
      if (parts.length < 2) return ''
      return `/${parts.slice(0, -1).join('/')}`
    })
    .filter(Boolean)

  const unique = [...new Set(parents)]
  return unique.length === 1 ? unique[0] : ''
}

/** 菜单节点有效路径：优先自身 path，否则从子菜单推导 */
export function resolveMenuNodePath(node) {
  const selfPath = getMenuNodePath(node)
  if (selfPath && selfPath !== HOME_PATH) return selfPath
  return deriveMenuPathFromChildren(node)
}

/** 判断 current 是否等于 base 或其子路径 */
export function isPathUnderMenu(currentPath, menuPath) {
  const current = normalizeContentPath(currentPath)
  const base = normalizeMenuPath(menuPath)
  if (!base || base === HOME_PATH) return false
  return current === base || current.startsWith(`${base}/`)
}

/** 提取 root 下的第一段路径（分类 slug 或详情 key） */
export function extractSegmentUnderRoot(path, rootPath) {
  const current = normalizeContentPath(path)
  const base = normalizeMenuPath(rootPath)
  if (!base || base === HOME_PATH) return slugFromMenuPath(current)
  if (current === base) return ''
  if (!current.startsWith(`${base}/`)) return ''
  const rest = current.slice(base.length + 1)
  return rest.split('/').filter(Boolean)[0] || ''
}

/** 在 root 下拼接子路径 */
export function joinMenuPath(rootPath, segment) {
  const base = normalizeMenuPath(rootPath)
  const part = String(segment || '').trim().replace(/^\/+|\/+$/g, '')
  if (!part) return base
  if (!base || base === HOME_PATH) return `/${part}`
  return `${base}/${part}`
}

/** 递归查找菜单树下第一个有效路径 */
export function findFirstNavigablePath(menuNode) {
  if (!menuNode) return ''
  const selfPath = getMenuNodePath(menuNode)
  if (selfPath && selfPath !== HOME_PATH) return selfPath

  const children = (menuNode.children || []).filter((item) => isPortalMenuVisible(item))
  for (const child of children) {
    const nested = findFirstNavigablePath(child)
    if (nested) return nested
  }
  return ''
}

/** 按 menuId 查找菜单 path */
export function findMenuPathByMenuId(menus = [], menuId) {
  const node = findMenuNodeById(menus, menuId)
  return node ? getMenuNodePath(node) : ''
}

/** 在顶级菜单中按 templateId 查找根菜单 */
export function findRootMenuByTemplateId(menus = [], templateId) {
  const id = Number(templateId)
  return (menus || []).find((menu) => {
    if (!isPortalMenuVisible(menu)) return false
    return Number(menu.templateId) === id
  }) || null
}

/** 在顶级菜单中按 contentType 查找根菜单 */
export function findRootMenuByContentType(menus = [], contentType) {
  if (!contentType) return null
  return (menus || []).find((menu) => {
    if (!isPortalMenuVisible(menu)) return false
    return menu.contentType === contentType
  }) || null
}
